// Angular
import { NgModule, ErrorHandler, APP_INITIALIZER, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';
import { CommonModule, DatePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Routes, Router } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
//import { HttpModule } from '@angular/http'
// core
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
// Third Party
import { AgGridModule } from 'ag-grid-angular';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
ModuleRegistry.registerModules([AllCommunityModule]); // Register AG Grid modules globally
import { AgChartsModule } from 'ag-charts-angular';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { SortableModule } from 'ngx-bootstrap/sortable';
import { AlertModule } from 'ngx-bootstrap/alert';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgxPlaidLinkModule } from 'ngx-plaid-link';
// Material Components
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';

import { ModalModule } from 'ngx-bootstrap/modal';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { NgxDropzoneModule } from 'ngx-dropzone';
//import { NgxMaskModule } from 'ngx-mask';
import { CKEditorModule } from 'ckeditor4-angular';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { ToastrModule } from 'ngx-toastr';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { FullCalendarModule } from '@fullcalendar/angular';

// Landing Page
import { LandingPageComponent } from './landing-page/landing-page.component';
import { AboutComponent } from './landing-page/about/about.component';
import { ContactComponent } from './landing-page/contact/contact.component';
import { CompetitorComparisonComponent } from './landing-page/competitor-comparison/competitor-comparison.component';
import { BottomFooterComponent } from './landing-page/bottom-footer/bottom-footer.component';
import { TopNavbarComponent } from './landing-page/top-navbar/top-navbar.component';
import { SignUpComponent } from './landing-page/sign-up/sign-up.component';

// A&D components
import { AcquisitionComponent } from './acquisitions/acquisition/acquisition.component';
import { AcquisitionBulkComponent } from './acquisitions/acquisition-bulk/acquisition-bulk.component';
import { BoundBookComponent } from './bound-book/bound-book/bound-book.component';
import { Buyer4473Component } from './form-4473/buyer-4473/buyer-4473.component';
import { DigitalSignatureComponent } from './shared/modals/digital-signature/digital-signature.component';
import { DispositionComponent } from './dispositions/disposition/disposition.component';
import { DispositionBuildComponent } from './dispositions/disposition-build/disposition-build.component';
import { DispoEditBtnsComponent } from './shared/components/ag-grid/dispo-edit-btns/dispo-edit-btns.component';
import { DispositionSelectionComponent } from './disposition-selection/disposition-selection.component';
import { FflManagementComponent } from './ffl/ffl-management/ffl-management.component';
import { FflManagementEditComponent } from './ffl/ffl-management/modals/ffl-management-edit/ffl-management-edit.component';
import { FirearmInfoDashComponent } from './shared/components/firearm-info-dash/firearm-info-dash.component';
import { FirearmManagementComponent } from './firearms/firearm-management/firearm-management.component';
import { FirearmManagementEditComponent } from './firearms/firearm-management/modals/firearm-management-edit/firearm-management-edit.component';
import { EditBbFirearmComponent } from './shared/modals/edit-bb-firearm/edit-bb-firearm.component';
import { EditBbAcquisitionComponent } from './shared/modals/edit-bb-acquisition/edit-bb-acquisition.component';
import { EditBbDispositionComponent } from './shared/modals/edit-bb-disposition/edit-bb-disposition.component';
import { HomeComponent } from './home/home.component';
import { LoginComponent } from './login/login.component';
import { QuestionnaireIntoComponent } from './form-4473/modals/questionnaire-into/questionnaire-into.component';
import { ScanIdComponent } from './shared/modals/scan-id/scan-id.component';
import { Seller4473Component } from './form-4473/seller-4473/seller-4473.component';
import { SerialNumberListComponent } from './shared/modals/serial-number-list/serial-number-list.component';
import { SideNavComponent } from './sidenav/sidenav.component';
import { StepProgressBarComponent } from './shared/components/step-progress-bar/step-progress-bar.component';
import { VendorDashboardComponent } from './vendor-data/vendor-dashboard/vendor-dashboard.component';
import { FflSearchComponent } from './shared/modals/ffl-search/ffl-search.component';
import { FflLookupComponent } from './ffl-lookup/ffl-lookup.component';
import { KioskComponent } from './kiosk/kiosk.component';
import { ReservedFirearmsComponent } from './reserved-firearms/reserved-firearms.component';
import { Reserved4473Component } from './reserved-4473/reserved-4473.component';
import { DriversLicenseSelectionComponent } from './shared/modals/drivers-license-selection/drivers-license-selection.component';
import { DashboardComponent } from './reporting/dashboard/dashboard.component';
import { InventoryOptionsComponent } from './shared/modals/inventory-options/inventory-options.component';
import { InventoryRequestListsComponent } from './vendor-data/inventory-request-lists/inventory-request-lists.component';
import { AutoPurchaseHistoryComponent } from './vendor-data/auto-purchase-history/auto-purchase-history.component';
import { EditDeleteBtnsComponent } from './shared/components/edit-delete-btns/edit-delete-btns.component';
import { ImportByCategoryComponent } from './accounting/products-services/modals/import-by-category/import-by-category.component';
import { UserCredentialsComponent } from './shared/modals/user-credentials/user-credentials.component';
import { ReserveInputsComponent } from './disposition-selection/modals/reserve-inputs/reserve-inputs.component';
import { DuplicateTtsnComponent } from './shared/modals/duplicate-ttsn/duplicate-ttsn.component';
import { DispoBuildComponent } from './shared/modals/dispo-build/dispo-build.component';

// Accounting components
import { AddEditUsersComponent } from './account-info/modals/add-edit-users/add-edit-users.component';
import { AddEditContactComponent } from './accounting/modals/add-edit-contact/add-edit-contact.component';
import { AccountManagementComponent } from './account-info/account-management/account-management.component';
import { AccountDashboardComponent } from './account-info/account-dashboard/account-dashboard.component';
import { AccountingOptionsComponent } from './account-info/accounting-options/accounting-options.component';
import { BankingComponent } from './accounting/banking/banking.component';
import { BoundBookBtnsComponent } from './shared/components/ag-grid/bound-book-btns/bound-book-btns.component';
import { ContactsTableComponent } from './accounting/contacts-table/contacts-table.component';
import { ChoiceComponent } from './shared/modals/choice/choice.component';
import { CreateProductServiceComponent } from './shared/modals/create-product-service/create-product-service.component';
import { ExpenseComponent } from './accounting/expense/expense.component';
import { ExpenseTableComponent } from './accounting/expense-table/expense-table.component';
import { PaymentComponent } from './account-info/payment/payment.component';
import { UserManagementComponent } from './account-info/user-management/user-management.component';
import { ProductsServicesComponent } from './accounting/products-services/products-services.component';
import { SalesComponent } from './accounting/sales/sales.component';
import { SalesReceiptPrintComponent } from './accounting/sales-receipt-print/sales-receipt-print.component';
import { SalesTableComponent } from './accounting/sales-table/sales-table.component';
import { DistributorManagementComponent } from './account-info/distributor-management/distributor-management.component';
import { AccountChangeLogComponent } from './account-info/account-change-log/account-change-log.component';
import { InventoryComponent } from './accounting/reporting/inventory/inventory.component';
// Vendor Data
import { CompetitorPricingComponent } from './vendor-data/vendor-dashboard/modals/competitor-pricing/competitor-pricing.component';
import { RegionalTransferReportingComponent } from './reporting/regional-transfer-reporting/regional-transfer-reporting.component';

// shared components
import { ToggleSwitchComponent } from './shared/components/toggle-switch/toggle-switch.component';
// Shared AG-Grid components
import { AddEditDeleteBtnsComponent } from './shared/components/ag-grid/add-edit-delete-btns/add-edit-delete-btns.component';
import { DeleteBtnComponent } from './shared/components/ag-grid/delete-btn/delete-btn.component';
import { EditBtnComponent } from './shared/components/ag-grid/edit-btn/edit-btn.component';
import { ProductServiceAutocompleteComponent } from './shared/components/ag-grid/product-service-autocomplete/product-service-autocomplete.component';
import { RevertBtnComponent } from './shared/components/ag-grid/revert-btn/revert-btn.component';
import { SelectBtnComponent } from './shared/components/ag-grid/select-btn/select-btn.component';
import { StateBadgeComponent } from './shared/components/ag-grid/state-badge/state-badge.component';
import { CustomFloatingFilterComponent } from './shared/components/ag-grid/custom-floating-filter.component';
import { CheckboxRenderer } from './shared/components/ag-grid/checkbox-renderer.component';
import { ChangePasswordComponent } from './shared/modals/change-password/change-password.component';
import { ForgotPasswordComponent } from './shared/modals/forgot-password/forgot-password.component';
import { LoginHelpComponent } from './shared/modals/login-help/login-help.component';
import { SyncQueueModalComponent } from './shared/modals/sync-queue/sync-queue-modal.component';

// modal components
import { YesNoComponent } from './shared/modals/yes-no/yes-no.component';
import { MessageComponent } from './shared/modals/message/message.component';
import { EnterIdComponent } from './shared/modals/enter-id/enter-id.component';

// services
import { AuthInterceptorService } from './shared/services/auth-interceptor.service';
import { CorrelationIdInterceptor } from './shared/correlation-id.interceptor';
import { GlobalErrorHandler } from './shared/global-error-handler';
import { HttpErrorInterceptor } from './shared/http-error.interceptor';
import { AuthGuard } from './shared/guards/auth.guard';
import { StartupService } from './services/startup.service';
import { SharedService } from './shared/services/shared.service';
import { ChatbotService } from './chatbot/chatbot.service';
import { FaqService } from './help/faq/faq.service';

// directives
import { OnlyNumbersDirective } from './shared/directives/only-numbers';
import { CapitalizeFirstDirective } from './shared/directives/capitalize-first.directive';
import { UppercaseDirective } from './shared/directives/uppercase.directive';
import { DateFormatDirective } from './shared/directives/date-format.directive';
import { SsnMaskDirective } from './shared/directives/ssn-mask.directive';

// pipes
import { ReplacePipe } from './shared/pipes/replace.pipe';
import { WordWrapPipe } from './shared/pipes/word-wrap.pipe';
import { HighlightPipe } from './shared/pipes/highlight.pipe';
import { CapitalizeFirstPipe } from './shared/pipes/capitalize-first.pipe';
//import { UppercasePipe } from './shared/pipes/uppercase.pipe';

// chatbot
import { ChatbotComponent } from './chatbot/chatbot.component';

// help
import { FaqComponent } from './help/faq/faq.component';
import { IncompleteDispositionsComponent } from './dispositions/incomplete-dispositions/incomplete-dispositions.component';
import { CardTemplateComponent } from './shared/components/card-template/card-template.component';
import { Form3310AutofillComponent } from './account-info/form-3310-autofill/form-3310-autofill.component';
import { ReCertBtnComponent } from './reserved-4473/re-cert-btn/re-cert-btn/re-cert-btn.component';
import { KioskSelectionComponent } from './kiosk/modals/kiosk-selection/kiosk-selection.component';
import { CreateForm3310Component } from './shared/modals/create-form-3310/create-form-3310.component';
import { FeaturesComponent } from './landing-page/features/features.component';
import { EmailConfigComponent } from './account-info/email-config/email-config.component';
import { EmailComponent } from './shared/modals/email/email.component';
import { TextModalComponent } from './shared/modals/text-modal/text-modal.component';
import { FflPackingSlipPrintComponent } from './dispositions/ffl-packing-slip-print/ffl-packing-slip-print.component';
import { AddBoundBookComponent } from './shared/modals/add-bound-book/add-bound-book.component';
import { BoundBookPrintComponent } from './bound-book/bound-book-print/bound-book-print.component';
import { CustomSpinnerComponent } from './shared/components/custom-spinner/custom-spinner.component';
import { InventorySearchComponent } from './shared/modals/inventory-search/inventory-search.component';
import { PurchaseOptimizerComponent } from './vendor-data/purchase-optimizer/purchase-optimizer.component';
import { OptimizerItemComponent } from './shared/modals/optimizer-item/optimizer-item.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CalendarComponent } from './calendar/calendar.component';
import { AddEditEventComponent } from './calendar/modals/add-edit-event/add-edit-event.component';
import { ProfitLossComponent } from './accounting/reporting/profit-loss/profit-loss.component';
import { SetupChecklistComponent } from './setup-checklist/setup-checklist.component';
import { BalanceSheetComponent } from './accounting/reporting/balance-sheet/balance-sheet.component';
import { CostOfGoodsSoldComponent } from './accounting/reporting/cost-of-goods-sold/cost-of-goods-sold.component';
import { SalesTaxLibilityComponent } from './accounting/reporting/sales-tax-libility/sales-tax-libility.component';
import { CashFlowStatmentComponent } from './accounting/reporting/cash-flow-statment/cash-flow-statment.component';
import { InventoryAlertsComponent } from './shared/modals/inventory-alerts/inventory-alerts.component';

const appRoutes: Routes = [
  { path: '', component: LoginComponent }, // localhost:4200/login
  { path: 'login', component: LoginComponent }, // localhost:4200/login
  
  // Guarded Routes 
  { path: 'home', component: HomeComponent, canActivate: [AuthGuard] },
  { path: 'acquisition', component: AcquisitionComponent, canActivate: [AuthGuard] },
  { path: 'acquisition-bulk', component: AcquisitionBulkComponent, canActivate: [AuthGuard] },
  { path: 'disposition', component: DispositionComponent, canActivate: [AuthGuard] },
  { path: 'disposition-selection', component: DispositionSelectionComponent, canActivate: [AuthGuard] },
  { path: 'disposition-build', component: DispositionBuildComponent, canActivate: [AuthGuard] },
  { path: 'pending-dispositions', component: IncompleteDispositionsComponent, canActivate: [AuthGuard] },
  { path: 'ffl-management', component: FflManagementComponent, canActivate: [AuthGuard] },
  { path: 'firearm-management', component: FirearmManagementComponent, canActivate: [AuthGuard] },
  { path: 'bound-book', component: BoundBookComponent, canActivate: [AuthGuard] },
  { path: 'buyer-form-4473', component: Buyer4473Component, canActivate: [AuthGuard] },
  { path: 'seller-form-4473', component: Seller4473Component, canActivate: [AuthGuard] },
  { path: 'digital-signature', component: DigitalSignatureComponent, canActivate: [AuthGuard] },
  { path: 'account-dashboard', component: AccountDashboardComponent, canActivate: [AuthGuard] },
  { path: 'account-management', component: AccountManagementComponent, canActivate: [AuthGuard] },
  { path: 'user-management', component: UserManagementComponent, canActivate: [AuthGuard] },
  { path: 'email-config', component: EmailConfigComponent, canActivate: [AuthGuard] },
  { path: 'accounting-expense', component: ExpenseComponent, canActivate: [AuthGuard] },
  { path: 'accounting-expense-table', component: ExpenseTableComponent, canActivate: [AuthGuard] },
  { path: 'accounting-sales-receipt', component: SalesComponent, canActivate: [AuthGuard] },
  { path: 'accounting-sales-table', component: SalesTableComponent, canActivate: [AuthGuard] },
  { path: 'accounting-contacts-table', component: ContactsTableComponent, canActivate: [AuthGuard] },
  { path: 'accounting-products-services', component: ProductsServicesComponent, canActivate: [AuthGuard] },
  { path: 'vendor-dashboard', component: VendorDashboardComponent, canActivate: [AuthGuard] },
  { path: 'inventory-request-lists', component: InventoryRequestListsComponent, canActivate: [AuthGuard] },
  { path: 'auto-purchase-history', component: AutoPurchaseHistoryComponent, canActivate: [AuthGuard] },
  { path: 'kiosk-mode', component: KioskComponent, canActivate: [AuthGuard] },
  { path: 'account-change-log', component: AccountChangeLogComponent, canActivate: [AuthGuard] },
  { path: 'distributor-management', component: DistributorManagementComponent, canActivate: [AuthGuard] },
  { path: 'drivers-license-selection', component: DriversLicenseSelectionComponent, canActivate: [AuthGuard] },
  { path: 'reserved-4473', component: Reserved4473Component, canActivate: [AuthGuard] },
  { path: 'reserved-firearms', component: ReservedFirearmsComponent, canActivate: [AuthGuard] },
  { path: 'faq', component: FaqComponent, canActivate: [AuthGuard] },
  { path: 'transfer-reporting', component: RegionalTransferReportingComponent, canActivate: [AuthGuard] },
  { path: 'banking', component: BankingComponent, canActivate: [AuthGuard] },
  { path: 'accounting-reports-inventory', component: InventoryComponent, canActivate: [AuthGuard] },

  { path: 'profit-loss', component: ProfitLossComponent, canActivate: [AuthGuard] },
  { path: 'balance-sheet', component: BalanceSheetComponent, canActivate: [AuthGuard] },
  { path: 'cost-of-goods-sold', component: CostOfGoodsSoldComponent, canActivate: [AuthGuard] },
  { path: 'sales-tax-libility', component: SalesTaxLibilityComponent, canActivate: [AuthGuard] },
  { path: 'cash-flow-statment', component: CashFlowStatmentComponent, canActivate: [AuthGuard] },

  { path: 'calendar', component: CalendarComponent, canActivate: [AuthGuard] },
  
  // Public Landing Page Routes
  { path: 'landing-page', component: LandingPageComponent },
  { path: 'landing-page-about', component: AboutComponent },
  { path: 'landing-page-contact', component: ContactComponent },
  { path: 'landing-page-competitor-comparison', component: CompetitorComparisonComponent },
  { path: 'landing-page-sign-up', component: SignUpComponent },
  { path: 'ffl-lookup', component: FflLookupComponent, canActivate: [AuthGuard] },
  { path: 'purchase-optimizer', component: PurchaseOptimizerComponent, canActivate: [AuthGuard] },
  { path: 'setup-checklist', component: SetupChecklistComponent, canActivate: [AuthGuard] },
];

@NgModule({
  declarations: [
    AppComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AgGridModule,
    AgChartsModule,
    AlertModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    BsDatepickerModule,
    BsDropdownModule,
    CardTemplateComponent,
    FflLookupComponent,
    FullCalendarModule,
    CKEditorModule,
    CarouselModule,
    CollapseModule,
    CommonModule,
    DragDropModule,
    SortableModule,
    FontAwesomeModule,
    FormsModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatAutocompleteModule,
    MatTooltipModule,
    MatStepperModule,
    MatCardModule,
    MatProgressBarModule,
    MatButtonModule,
    MatRadioModule,
    ModalModule,
    NgSelectModule,
    NgxDropzoneModule,
    NgxMaskDirective,
    NgxMaskPipe,
    PaginationModule,
    NgxPlaidLinkModule,
    ProgressbarModule,
    ReactiveFormsModule,
    RouterModule.forRoot(appRoutes),
    TabsModule,
    TimepickerModule,
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      enableHtml: true,
    }),
    TooltipModule,
    TypeaheadModule,
    CustomSpinnerComponent,
    FullCalendarModule,
    // Phase 1 standalone components
    AboutComponent,
    AddEditDeleteBtnsComponent,
    BottomFooterComponent,
    BoundBookBtnsComponent,
    CheckboxRenderer,
    CompetitorComparisonComponent,
    ContactComponent,
    CustomFloatingFilterComponent,
    DeleteBtnComponent,
    DispoEditBtnsComponent,
    EditBtnComponent,
    EditDeleteBtnsComponent,
    FeaturesComponent,
    FirearmInfoDashComponent,
    LandingPageComponent,
    ProductServiceAutocompleteComponent,
    RevertBtnComponent,
    SelectBtnComponent,
    SignUpComponent,
    StateBadgeComponent,
    StepProgressBarComponent,
    ToggleSwitchComponent,
    TopNavbarComponent,
    // Phase 2 standalone components
    AddBoundBookComponent,
    ChangePasswordComponent,
    ChoiceComponent,
    CompetitorPricingComponent,
    CreateForm3310Component,
    CreateProductServiceComponent,
    DashboardComponent,
    DigitalSignatureComponent,
    DriversLicenseSelectionComponent,
    DuplicateTtsnComponent,
    EditBbAcquisitionComponent,
    EditBbDispositionComponent,
    EditBbFirearmComponent,
    EmailComponent,
    EnterIdComponent,
    FaqComponent,
    FflSearchComponent,
    ForgotPasswordComponent,
    HighlightPipe,
    InventoryAlertsComponent,
    InventoryOptionsComponent,
    InventorySearchComponent,
    LoginHelpComponent,
    MessageComponent,
    OptimizerItemComponent,
    RegionalTransferReportingComponent,
    ScanIdComponent,
    SerialNumberListComponent,
    SyncQueueModalComponent,
    TextModalComponent,
    UserCredentialsComponent,
    YesNoComponent,
    // Phase 3 standalone components
    AccountChangeLogComponent,
    AccountDashboardComponent,
    AccountManagementComponent,
    AccountingOptionsComponent,
    AddEditEventComponent,
    AddEditUsersComponent,
    AutoPurchaseHistoryComponent,
    CalendarComponent,
    ChatbotComponent,
    DistributorManagementComponent,
    EmailConfigComponent,
    FflManagementComponent,
    FflManagementEditComponent,
    FirearmManagementComponent,
    FirearmManagementEditComponent,
    Form3310AutofillComponent,
    InventoryRequestListsComponent,
    PaymentComponent,
    PurchaseOptimizerComponent,
    ReplacePipe,
    UserManagementComponent,
    VendorDashboardComponent,
    // Phase 4 standalone components
    AddEditContactComponent,
    BankingComponent,
    BalanceSheetComponent,
    CashFlowStatmentComponent,
    ContactsTableComponent,
    CostOfGoodsSoldComponent,
    ExpenseComponent,
    ExpenseTableComponent,
    ImportByCategoryComponent,
    InventoryComponent,
    ProductsServicesComponent,
    ProfitLossComponent,
    ReCertBtnComponent,
    Reserved4473Component,
    ReservedFirearmsComponent,
    SalesComponent,
    SalesReceiptPrintComponent,
    SalesTaxLibilityComponent,
    SalesTableComponent,
    // Phase 4 pipes
    CapitalizeFirstPipe,
    WordWrapPipe,
    // Phase 5 standalone components
    AcquisitionComponent,
    AcquisitionBulkComponent,
    BoundBookComponent,
    BoundBookPrintComponent,
    Buyer4473Component,
    DispositionBuildComponent,
    DispositionComponent,
    DispositionSelectionComponent,
    FflPackingSlipPrintComponent,
    IncompleteDispositionsComponent,
    KioskComponent,
    KioskSelectionComponent,
    QuestionnaireIntoComponent,
    ReserveInputsComponent,
    Seller4473Component,
    SsnMaskDirective,
    // Phase 6 standalone components and directives
    CapitalizeFirstDirective,
    DateFormatDirective,
    DispoBuildComponent,
    HomeComponent,
    LoginComponent,
    OnlyNumbersDirective,
    SetupChecklistComponent,
    SideNavComponent,
    UppercaseDirective,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CorrelationIdInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    provideNgxMask(),
    StartupService,
    SharedService,
    ChatbotService,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    Sentry.TraceService,
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
})
export class AppModule {}
